import { inject, Injectable } from '@angular/core';
import { AppLang, LocaleService } from './locale-service';
import { format, format as dfFormat } from 'date-fns';

export type LocalizationDatePattern = 'short' | 'long';

/**
 * Transform data into the end user locale's format.
 */
@Injectable()
export class LocalizationService {
  private readonly localeService = inject(LocaleService);
  readonly locale$ = this.localeService.language$;

  /**
   * Format a date in a locale-aware way.
   *
   * This is similar to angular date pipe usage `{{ myDate | date:'shortDate' }}`
   *
   * @param date - Date object
   * @param pattern - if 'short' then the output will be similar to 120/11/2025
   *  if 'long' then the output will be similar to 12 novembre 2025
   */
  localizeDate(date: Date | null | undefined, pattern: LocalizationDatePattern = 'short'): string {
    const locale = this.localeService.getCurrentDateFns();
    const dateFnsPattern = pattern === 'short' ? 'P' : 'PPP';
    return date ? dfFormat(date, dateFnsPattern, { locale }) : '';
  }

  getMonthNames(): string[] {
    const pattern = 'LLLL';
    return Array.from({ length: 12 }, (_, i) => this.fnsFormat(new Date(2017, i, 1), pattern));
  }

  getMonthName(date: Date): string {
    const pattern = 'LLLL';
    return this.fnsFormat(date, pattern);
  }

  /**
   * Returns the application's current language.
   *
   * This method intentionally re-exposes `LocaleService.getCurrentLang()` for
   * consumers that already depend on `LocalizationService`.
   *
   * By doing so, callers do not need to import both `LocalizationService`
   * and `LocaleService`, resulting in a simpler and more convenient API surface.
   */
  getCurrentLang(): AppLang {
    return this.localeService.getCurrentLang();
  }

  private fnsFormat(date: Date, pattern: string) {
    return format(date, pattern, { locale: this.localeService.getCurrentDateFns() });
  }
}
